"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Check, Pipette, X } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { cn } from "@/lib/utils";

const SWATCH_COLORS = [
  "#FF9600",
  "#2196F3",
  "#26A69A",
  "#EF5350",
  "#FFA500",
  "#00C853",
  "#E040FB",
  "#9E9E9E",
  "#FFFFFF",
  "#FDD835",
  "#AB47BC",
  "#29B6F6",
  "#66BB6A",
  "#FF7043",
  "#5C6BC0",
  "#8D6E63",
];

export function normalizeHex(raw: string): string | null {
  const trimmed = raw.trim().replace(/^#/, "");
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  if (/^[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const expanded = trimmed
      .split("")
      .map((c) => c + c)
      .join("");
    return `#${expanded.toUpperCase()}`;
  }
  return null;
}

function hexForNativeInput(hex: string): string {
  const n = normalizeHex(hex);
  if (n) return n.toLowerCase();
  return "#ff9600";
}

interface IndicatorColorPickerProps {
  label: string;
  color: string;
  onApply: (color: string) => void;
  onCancel: () => void;
  onPreview?: (color: string) => void;
}

export default function IndicatorColorPicker({
  label,
  color,
  onApply,
  onCancel,
  onPreview,
}: IndicatorColorPickerProps) {
  const panelId = useId();
  const hexInputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(() => normalizeHex(color) ?? "#FF9600");
  const [hexInput, setHexInput] = useState(() =>
    (normalizeHex(color) ?? "#FF9600").replace(/^#/, "")
  );
  const [hexError, setHexError] = useState(false);

  useEffect(() => {
    const initial = normalizeHex(color) ?? "#FF9600";
    setDraft(initial);
    setHexInput(initial.replace(/^#/, ""));
    setHexError(false);
  }, [color]);

  useEffect(() => {
    hexInputRef.current?.focus();
    hexInputRef.current?.select();
  }, []);

  const commitDraft = useCallback(
    (next: string) => {
      const hex = normalizeHex(next);
      if (!hex) return;
      setDraft(hex);
      setHexInput(hex.replace(/^#/, ""));
      setHexError(false);
      onPreview?.(hex);
    },
    [onPreview]
  );

  const handleHexInputChange = (value: string) => {
    const cleaned = value.replace(/[^0-9A-Fa-f#]/g, "").replace(/^#/, "");
    setHexInput(cleaned);
    const hex = normalizeHex(cleaned);
    if (hex) {
      setHexError(false);
      setDraft(hex);
      onPreview?.(hex);
    } else if (cleaned.length > 0) {
      setHexError(true);
    } else {
      setHexError(false);
    }
  };

  const handleApply = () => {
    const hex = normalizeHex(hexInput) ?? draft;
    onApply(hex);
  };

  return (
    <div
      id={panelId}
      role="region"
      aria-label={`Custom color for ${label}`}
      className="mt-2 rounded-lg border border-border bg-muted/30 overflow-hidden animate-in fade-in-0 slide-in-from-top-1 duration-200"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/60 bg-muted/40">
        <span className="text-xs font-medium text-foreground">
          Custom · {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onCancel}
          aria-label="Close color picker"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="p-3 space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="h-12 w-12 shrink-0 rounded-md border border-border shadow-inner"
            style={{ backgroundColor: draft }}
            aria-hidden
          />
          <div className="flex-1 min-w-0 space-y-1.5">
            <Label
              htmlFor={`${panelId}-hex`}
              className="text-xs text-muted-foreground"
            >
              Hex color
            </Label>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground font-mono">#</span>
              <Input
                id={`${panelId}-hex`}
                ref={hexInputRef}
                value={hexInput}
                onChange={(e) => handleHexInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleApply();
                  if (e.key === "Escape") onCancel();
                }}
                maxLength={6}
                spellCheck={false}
                className={cn(
                  "h-8 font-mono text-sm uppercase flex-1",
                  hexError &&
                    "border-destructive focus-visible:ring-destructive"
                )}
                aria-invalid={hexError}
                aria-describedby={
                  hexError ? `${panelId}-hex-error` : undefined
                }
              />
            </div>
            {hexError ? (
              <p
                id={`${panelId}-hex-error`}
                className="text-[11px] text-destructive"
              >
                Enter a valid 6-digit hex code
              </p>
            ) : null}
          </div>
        </div>

        <label className="relative flex items-center justify-center gap-2 h-9 rounded-md border border-dashed border-border bg-background/50 cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden">
          <Pipette className="h-4 w-4 text-muted-foreground pointer-events-none" />
          <span className="text-xs text-muted-foreground pointer-events-none">
            System color picker
          </span>
          <input
            type="color"
            value={hexForNativeInput(draft)}
            onChange={(e) => commitDraft(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label="Open system color picker"
          />
        </label>

        <div className="grid grid-cols-8 gap-1.5">
          {SWATCH_COLORS.map((swatch) => {
            const selected = draft.toLowerCase() === swatch.toLowerCase();
            return (
              <button
                key={swatch}
                type="button"
                onClick={() => commitDraft(swatch)}
                className={cn(
                  "aspect-square rounded-md border-2 transition-all hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected
                    ? "border-primary ring-2 ring-primary/30 scale-105"
                    : "border-transparent"
                )}
                style={{ backgroundColor: swatch }}
                aria-label={`Color ${swatch}`}
                aria-pressed={selected}
              />
            );
          })}
        </div>
      </div>

      <div className="flex gap-2 px-3 py-2.5 border-t border-border/60 bg-muted/20">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className="flex-1 gap-1.5"
          onClick={handleApply}
          disabled={hexError}
        >
          <Check className="h-3.5 w-3.5" />
          Apply
        </Button>
      </div>
    </div>
  );
}
